'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksYanEtkileriPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoks-yan-etkileri';
  const pageTitle = 'Botoksun Yan Etkileri ve Riskleri - Bilmeniz Gerekenler Neler?';
  const pageDescription = 'Botoks yan etkileri nelerdir? Riskler nasil azaltilir? Botoks uygulamasi oncesi bilinmesi gereken tum yan etkiler ve guvenlik onlemleri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoksun Yan Etkileri ve Riskleri', href: '/tr/blog/botoks-yan-etkileri' },
            ]}
          />
        </div>

        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks yan etkileri ve dikkat edilmesi gerekenler"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Estetik Tedaviler
              </span>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoksun Yan Etkileri ve Riskleri - Bilmeniz Gerekenler Neler?
            </h1>

            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Baris Cin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>18 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylas:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks, estetik ve medikal amaclarla sikca tercih edilen guvenli bir uygulamadir. Ancak her tibbi islemde oldugu gibi, botoksun da bazi yan etkileri ve nadiren de olsa ciddi riskleri olabilir. Bu yazida botoks yaptirmadan once bilinmesi gereken tum olasi etkileri detayli sekilde ele aldik.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botoks Uygulamasi Ne Kadar Guvenli?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Botulinum toksini, dogru dozda ve deneyimli hekim tarafindan uygulandiginda genellikle son derece guvenlidir. FDA (Amerikan Gida ve Ilac Dairesi) onaylidir ve dunya capinda milyonlarca kisi tarafindan kullanilmaktadir. Ancak uygulama hatalari, yanlis doz ya da bireysel hassasiyetler bazi sorunlara yol acabilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botoksun Yaygin (Gecici) Yan Etkileri
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu etkiler genellikle hafif ve gecicidir. Islemden sonraki birkac saat ile birkac gun icinde gorulur:
              </p>

              <div className="bg-yellow-50 rounded-lg p-6 mb-6">
                <ul className="space-y-2 text-gray-800">
                  <li>• Kizariklik ve hafif sislik (enjeksiyon bolgelerinde)</li>
                  <li>• Morarma veya hafif hassasiyet</li>
                  <li>• Bas agrisi</li>
                  <li>• Uygulama bolgesinde hafif uyusma hissi</li>
                  <li>• Gecici asimetri veya kas dusukugu</li>
                </ul>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg mb-8">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Onemli:</strong> Bu etkiler genellikle 1-3 gun icinde kendiliginden gecer. Morarma icin buz uygulamasi onerilir.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Nadir Gorulen Yan Etkiler ve Riskler
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu yan etkiler cok daha az gorulur ancak dikkate alinmalidir:
              </p>
              
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Yan Etki / Risk
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Aciklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Goz kapagi dusukugu (ptozis)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Botoksun yanlis kaslara yayilmasi sonucu olusabilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Cift gorme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Goz cevresi botoksunda nadiren gorulebilir</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Yutma guclugu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Boyun veya cene bolgesi botoksunda asiri doz riski</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Kas zayifligi (uzak bolgede)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Toksinin yayilmasi sonucu olabilir (cok nadirdir)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Alerjik reaksiyon (kasinmi, dokunmu)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Botulinum toksinine hassasiyeti olanlarda gorulebilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Konusma bozuklugu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Agiz cevresi botoksunda nadiren</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Riskleri Azaltmak Icin Ne Yapmali?
              </h2>

              <div className="bg-green-50 rounded-lg p-6 mb-8">
                <ul className="space-y-3 text-green-800">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Sadece uzman doktorlar tarafindan uygulama yapilmali
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Saglik Bakanligi onayli orijinal botoks urunleri kullanilmali
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Islem oncesi tum saglik gecmisi doktora aktarilmali
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    Uygulama sonrasi doktorun onerdigi kurallara uyulmali
                  </li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kimler Icin Risklidir?
              </h2>

              <div className="bg-red-50 rounded-lg p-6 mb-8">
                <ul className="space-y-3 text-red-800">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">⚠</span>
                    Hamileler ve emziren anneler
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">⚠</span>
                    Noromuskuler hastaligi olanlar
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">⚠</span>
                    Botulinum toksinine alerjisi olanlar
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">⚠</span>
                    Enjeksiyon yapilacak bolgede aktif enfeksiyon bulunanlar
                  </li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sik Sorulan Sorular
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    1. Yan etki yasarsam ne yapmaliym?
                  </h3>
                  <p className="text-gray-700">
                    Ciddi bir durum olmadikca genelde dinlenme ve buz uygulamasi yeterlidir. Ancak goz kapagi dusukugu, yutma guclugu gibi belirtiler varsa hekime basvurulmalidir.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    2. Botoks kalici zarar verir mi?
                  </h3>
                  <p className="text-gray-700">
                    Hayir. Uygulama gecicidir. Etkiler genellikle 4-6 ay sonra tamamen kaybolur. Kalici etki nadirdir.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    3. Ayni yan etkiler her uygulamada olur mu?
                  </h3>
                  <p className="text-gray-700">
                    Hayir. Ilk uygulamada yan etki gorulse bile sonraki islemlerde gorulmeyebilir ya da daha hafif gecebilir.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">
                  Botoks Yan Etkileri Hakkinda Daha Fazla Bilgi Almak Ister misiniz?
                </h3>
                <p className="text-lg mb-6 opacity-90">
                  Op. Dr. Baris Cin ile guvenli botoks uygulamasi icin ucretsiz konsultasyon alin.
                </p>
                <a
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Merhaba, botoks yan etkileri hakkinda bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-8 rounded-full transition-colors duration-200"
                >
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.690z"/>
                  </svg>
                  WhatsApp ile Randevu Al
                </a>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Kaynakca</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Mayo Clinic - Botulinum Toxin Side Effects</li>
                    <li>• FDA Botulinum Toxin Safety Guide</li>
                    <li>• Turk Dermatoloji Dernegi - Uygulama Protokolleri</li>
                    <li>• 2024-2026 Klinik Hasta Bilgilendirme Formlari</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
